"use client";

import { Camera, Eye, EyeOff, Video as VideoIcon } from "lucide-react";
import { useEffect, useState } from "react";

const STORAGE_KEY_DEBUG_MODE = "kiosk-video-debug-mode";
const STORAGE_KEY_ANGLE_RANGES = "kiosk-video-angle-ranges";
const STORAGE_KEY_CAMERA_DEVICE = "kiosk-selected-camera-device";
const STORAGE_KEY_STABILITY_BUFFER = "kiosk-stability-buffer-ms";
const STORAGE_KEY_STABILITY_REQUIRED = "kiosk-stability-required-ms";

// Camera label preferences for fallback selection (in order of priority)
const PREFERRED_CAMERA_LABELS = [
  "front camera",
  "facetime",
  "back camera", // Standard back camera (not ultrawide)
];
const AVOID_CAMERA_LABELS = ["ultra wide", "ultrawide", "wide angle"];

/**
 * Find the best camera from a list of devices based on label preferences
 */
function findPreferredCameraDevice(
  devices: MediaDeviceInfo[],
): MediaDeviceInfo | null {
  // Debug: log all device labels
  console.log(
    "[VideoTab] Finding preferred camera from devices:",
    devices.map((d) => ({ label: d.label, deviceId: d.deviceId.slice(0, 8) })),
  );

  // First, try to find a preferred camera by label
  for (const preferred of PREFERRED_CAMERA_LABELS) {
    const match = devices.find(
      (d) =>
        d.label.toLowerCase().includes(preferred.toLowerCase()) &&
        !AVOID_CAMERA_LABELS.some((avoid) =>
          d.label.toLowerCase().includes(avoid.toLowerCase()),
        ),
    );
    if (match) {
      console.log(
        `[VideoTab] Found preferred camera matching "${preferred}":`,
        match.label,
      );
      return match;
    }
  }

  // If no preferred camera found, pick first non-ultrawide camera
  const nonUltrawide = devices.find(
    (d) =>
      !AVOID_CAMERA_LABELS.some((avoid) =>
        d.label.toLowerCase().includes(avoid.toLowerCase()),
      ),
  );
  if (nonUltrawide) {
    console.log("[VideoTab] Using non-ultrawide fallback:", nonUltrawide.label);
    return nonUltrawide;
  }

  // Last resort: first available camera
  console.log("[VideoTab] Last resort - first camera:", devices[0]?.label);
  return devices[0] || null;
}

interface AngleRange {
  start: number;
  end: number;
  step: number;
}

const DEFAULT_ANGLE_RANGES: AngleRange[] = [
  { start: 0, end: 15, step: 1 }, // Vertical lines near 0°
  { start: 75, end: 105, step: 1 }, // Horizontal lines near 90°
  { start: 165, end: 180, step: 1 }, // Vertical lines near 180°
];

export function VideoTab() {
  const [debugMode, setDebugMode] = useState(false);
  const [angleRanges, setAngleRanges] =
    useState<AngleRange[]>(DEFAULT_ANGLE_RANGES);
  const [cameraDevices, setCameraDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const [isLoadingDevices, setIsLoadingDevices] = useState(true);
  const [bufferMs, setBufferMs] = useState(1000); // Default: 1000ms (1 second)
  const [requiredStableMs, setRequiredStableMs] = useState(2000); // Default: 2000ms (2 seconds)

  // Load settings from localStorage on mount
  useEffect(() => {
    const storedDebugMode = localStorage.getItem(STORAGE_KEY_DEBUG_MODE);
    if (storedDebugMode !== null) {
      setDebugMode(storedDebugMode === "true");
    }

    const storedAngleRanges = localStorage.getItem(STORAGE_KEY_ANGLE_RANGES);
    if (storedAngleRanges) {
      try {
        setAngleRanges(JSON.parse(storedAngleRanges));
      } catch (e) {
        console.error("Failed to parse angle ranges from localStorage:", e);
      }
    }

    const storedDeviceId = localStorage.getItem(STORAGE_KEY_CAMERA_DEVICE);
    if (storedDeviceId) {
      setSelectedDeviceId(storedDeviceId);
    }

    const storedBufferMs = localStorage.getItem(STORAGE_KEY_STABILITY_BUFFER);
    if (storedBufferMs) {
      setBufferMs(Number.parseInt(storedBufferMs));
    }

    const storedRequiredMs = localStorage.getItem(
      STORAGE_KEY_STABILITY_REQUIRED,
    );
    if (storedRequiredMs) {
      setRequiredStableMs(Number.parseInt(storedRequiredMs));
    }
  }, []);

  // Enumerate camera devices
  useEffect(() => {
    const loadDevices = async () => {
      try {
        // Request permission first (required on some browsers to get device labels)
        await navigator.mediaDevices.getUserMedia({ video: true });

        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(
          (device) => device.kind === "videoinput",
        );
        setCameraDevices(videoDevices);

        // Set default if none selected - check localStorage directly to avoid race condition
        const storedDeviceId = localStorage.getItem(STORAGE_KEY_CAMERA_DEVICE);
        if (
          storedDeviceId &&
          videoDevices.some((d) => d.deviceId === storedDeviceId)
        ) {
          // Use the stored device if it's still available
          setSelectedDeviceId(storedDeviceId);
        } else if (videoDevices.length > 0) {
          // Fall back to preferred camera (front camera, avoiding ultrawide)
          const preferred = findPreferredCameraDevice(videoDevices);
          if (preferred) {
            setSelectedDeviceId(preferred.deviceId);
            // Persist the selection so camera provider picks it up
            localStorage.setItem(STORAGE_KEY_CAMERA_DEVICE, preferred.deviceId);
          }
        }
      } catch (error) {
        console.error("Failed to enumerate camera devices:", error);
      } finally {
        setIsLoadingDevices(false);
      }
    };

    loadDevices();
  }, []);

  const handleDebugModeChange = (enabled: boolean) => {
    setDebugMode(enabled);
    localStorage.setItem(STORAGE_KEY_DEBUG_MODE, String(enabled));
    // Trigger custom event to notify PhotoCapture component
    window.dispatchEvent(
      new CustomEvent("kiosk-video-settings-changed", {
        detail: { debugMode: enabled, angleRanges },
      }),
    );
  };

  const handleAngleRangeChange = (
    index: number,
    field: keyof AngleRange,
    value: number,
  ) => {
    const newRanges = [...angleRanges];
    newRanges[index] = { ...newRanges[index], [field]: value };
    setAngleRanges(newRanges);
    localStorage.setItem(STORAGE_KEY_ANGLE_RANGES, JSON.stringify(newRanges));
    // Trigger custom event to notify card-detection
    window.dispatchEvent(
      new CustomEvent("kiosk-video-settings-changed", {
        detail: { debugMode, angleRanges: newRanges },
      }),
    );
  };

  const handleResetToDefaults = () => {
    setAngleRanges(DEFAULT_ANGLE_RANGES);
    localStorage.setItem(
      STORAGE_KEY_ANGLE_RANGES,
      JSON.stringify(DEFAULT_ANGLE_RANGES),
    );
    window.dispatchEvent(
      new CustomEvent("kiosk-video-settings-changed", {
        detail: { debugMode, angleRanges: DEFAULT_ANGLE_RANGES },
      }),
    );
  };

  const handleCameraDeviceChange = (deviceId: string) => {
    setSelectedDeviceId(deviceId);
    localStorage.setItem(STORAGE_KEY_CAMERA_DEVICE, deviceId);
    window.dispatchEvent(
      new CustomEvent("kiosk-video-settings-changed", {
        detail: { debugMode, angleRanges, selectedCameraDeviceId: deviceId },
      }),
    );
  };

  const handleStabilityChange = (buffer: number, required: number) => {
    setBufferMs(buffer);
    setRequiredStableMs(required);
    localStorage.setItem(STORAGE_KEY_STABILITY_BUFFER, String(buffer));
    localStorage.setItem(STORAGE_KEY_STABILITY_REQUIRED, String(required));
    window.dispatchEvent(
      new CustomEvent("kiosk-video-settings-changed", {
        detail: { bufferMs: buffer, requiredStableMs: required },
      }),
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b">
        <Camera className="h-6 w-6 text-blue-600" />
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Video Detection Settings
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Configure card detection parameters for PhotoCapture component
          </p>
        </div>
      </div>

      {/* Camera Device Selection */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center gap-3 mb-3">
          <VideoIcon className="h-5 w-5 text-blue-600" />
          <div className="flex-1">
            <h3 className="font-medium text-gray-900">Camera Device</h3>
            <p className="text-sm text-gray-600">
              Select which camera to use (iPad has multiple cameras)
            </p>
          </div>
        </div>

        {isLoadingDevices ? (
          <div className="text-sm text-gray-600">Loading cameras...</div>
        ) : cameraDevices.length === 0 ? (
          <div className="text-sm text-red-600">
            No cameras found. Please grant camera permissions.
          </div>
        ) : (
          <select
            value={selectedDeviceId}
            onChange={(e) => handleCameraDeviceChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {cameraDevices.map((device) => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label || `Camera ${device.deviceId.substring(0, 8)}...`}
              </option>
            ))}
          </select>
        )}

        {cameraDevices.length > 0 && (
          <div className="mt-2 text-xs text-gray-500">
            {cameraDevices.length} camera{cameraDevices.length !== 1 ? "s" : ""}{" "}
            detected
          </div>
        )}

        {/* Debug: Show all camera labels */}
        {cameraDevices.length > 0 && (
          <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
            <div className="font-medium text-yellow-800 mb-1">
              Debug: Camera Labels
            </div>
            <ul className="space-y-1 text-yellow-700">
              {cameraDevices.map((device, i) => (
                <li key={device.deviceId}>
                  {i + 1}. {device.label || "(empty label)"}{" "}
                  {device.deviceId === selectedDeviceId && (
                    <span className="text-green-600 font-medium">
                      ← selected
                    </span>
                  )}
                </li>
              ))}
            </ul>
            <div className="mt-2 pt-2 border-t border-yellow-200 text-yellow-600">
              Preference order: {PREFERRED_CAMERA_LABELS.join(" → ")}
            </div>
          </div>
        )}
      </div>

      {/* Debug Mode Toggle */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {debugMode ? (
              <Eye className="h-5 w-5 text-blue-600" />
            ) : (
              <EyeOff className="h-5 w-5 text-gray-400" />
            )}
            <div>
              <h3 className="font-medium text-gray-900">Debug Visualization</h3>
              <p className="text-sm text-gray-600">
                Show debug canvas and metrics overlay during card capture
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={debugMode}
              onChange={(e) => handleDebugModeChange(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
          </label>
        </div>
      </div>

      {/* Angle Ranges */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900">
              Hough Transform Angle Ranges
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Define which angles to search for during line detection (in
              degrees)
            </p>
          </div>
          <button
            type="button"
            onClick={handleResetToDefaults}
            className="px-3 py-1 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-200 rounded-md hover:bg-blue-50 transition-colors"
          >
            Reset to Defaults
          </button>
        </div>

        <div className="space-y-3">
          {angleRanges.map((range, index) => (
            <div
              key={`${range.start}-${range.end}`}
              className="bg-white border border-gray-200 rounded-lg p-4"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-medium text-gray-700">
                  Range {index + 1}
                </span>
                <span className="text-xs text-gray-500">
                  {index === 1 ? "(Horizontal lines ~90°)" : "(Vertical lines)"}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="block text-xs font-medium text-gray-600 mb-1">
                    Start (°)
                  </p>
                  <input
                    type="number"
                    value={range.start}
                    onChange={(e) =>
                      handleAngleRangeChange(
                        index,
                        "start",
                        Number.parseInt(e.target.value),
                      )
                    }
                    min={0}
                    max={180}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <p className="block text-xs font-medium text-gray-600 mb-1">
                    End (°)
                  </p>
                  <input
                    type="number"
                    value={range.end}
                    onChange={(e) =>
                      handleAngleRangeChange(
                        index,
                        "end",
                        Number.parseInt(e.target.value),
                      )
                    }
                    min={0}
                    max={180}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <p className="block text-xs font-medium text-gray-600 mb-1">
                    Step (°)
                  </p>
                  <input
                    type="number"
                    value={range.step}
                    onChange={(e) =>
                      handleAngleRangeChange(
                        index,
                        "step",
                        Number.parseInt(e.target.value),
                      )
                    }
                    min={1}
                    max={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Stability Settings */}
      <div className="space-y-4">
        <div>
          <h3 className="font-medium text-gray-900">
            Stability Tracker Settings (Time-Based)
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Configure how long the card must be held steady before auto-capture
            (works consistently across all devices)
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <label
              htmlFor="buffer-ms"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Buffer Duration (ms)
            </label>
            <input
              id="buffer-ms"
              type="number"
              value={bufferMs}
              onChange={(e) =>
                handleStabilityChange(
                  Number.parseInt(e.target.value),
                  requiredStableMs,
                )
              }
              min={0}
              max={5000}
              step={100}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Initial detection buffer ({(bufferMs / 1000).toFixed(1)}s)
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <label
              htmlFor="required-stable-ms"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Required Hold Time (ms)
            </label>
            <input
              id="required-stable-ms"
              type="number"
              value={requiredStableMs}
              onChange={(e) =>
                handleStabilityChange(bufferMs, Number.parseInt(e.target.value))
              }
              min={500}
              max={10000}
              step={100}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Hold time needed ({(requiredStableMs / 1000).toFixed(1)}s)
            </p>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">
          📘 How Angle Ranges Work
        </h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>
            • <strong>0° / 180°</strong> = Vertical lines (card left/right
            edges)
          </li>
          <li>
            • <strong>90°</strong> = Horizontal lines (card top/bottom edges)
          </li>
          <li>
            • <strong>Step</strong> controls angle resolution (smaller = more
            precise, slower)
          </li>
          <li>
            • Settings persist in localStorage and apply immediately to card
            detection
          </li>
        </ul>
      </div>
    </div>
  );
}
